import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 2000;
const MAX_POLLS = 30; // 60s

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "checking", data: null });
  const attemptsRef = useRef(0);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error", data: null }); return; }
    let cancelled = false;
    async function tick() {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) {
            setStatus({ state: "success", data });
            refresh();
          }
          return;
        }
        if (["failed", "expired", "canceled"].includes(data.payment_status)) {
          if (!cancelled) setStatus({ state: "failed", data });
          return;
        }
      } catch {}
      if (attemptsRef.current >= MAX_POLLS) {
        if (!cancelled) setStatus({ state: "timeout", data: null });
        return;
      }
      setTimeout(tick, POLL_MS);
    }
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24">
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-xl p-8 sm:p-10 text-center" data-testid="payment-success-card">
        {status.state === "checking" && (
          <>
            <div className="w-14 h-14 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto mb-5">
              <Loader2 className="w-6 h-6 text-cyan-signal animate-spin" />
            </div>
            <h1 className="text-2xl font-display font-bold">Confirming your payment…</h1>
            <p className="mt-2 text-slate-400 text-sm">Talking to Stripe. This usually takes a few seconds.</p>
          </>
        )}
        {status.state === "success" && (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight">You're in.</h1>
            <p className="mt-2 text-slate-400">You upgraded to <span className="text-cyan-signal font-medium capitalize">{status.data?.plan}</span> — <span className="font-mono">{(status.data?.credits || 0).toLocaleString()}</span> credits just landed.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard"><Button className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold" data-testid="btn-payment-success-dashboard">Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/console"><Button variant="outline" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800"><Sparkles className="w-4 h-4 mr-2" /> Run a lookup</Button></Link>
            </div>
          </>
        )}
        {(status.state === "failed" || status.state === "error") && (
          <>
            <div className="w-14 h-14 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-7 h-7 text-rose-400" />
            </div>
            <h1 className="text-2xl font-display font-bold">Payment didn't go through</h1>
            <p className="mt-2 text-slate-400 text-sm">Nothing was charged. You can try again from the pricing page.</p>
            <div className="mt-6"><Link to="/pricing"><Button variant="outline">Back to pricing</Button></Link></div>
          </>
        )}
        {status.state === "timeout" && (
          <>
            <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-5">
              <Loader2 className="w-6 h-6 text-amber-400" />
            </div>
            <h1 className="text-2xl font-display font-bold">Still processing…</h1>
            <p className="mt-2 text-slate-400 text-sm">Stripe is taking longer than usual. Your dashboard will update automatically.</p>
            <div className="mt-6"><Link to="/dashboard"><Button className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold">Go to Dashboard</Button></Link></div>
          </>
        )}
      </div>
    </div>
  );
}
